import {useCallback} from 'react';
import {assignProps} from '@sail/engine';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {option, useFocusOutside} from './Popover.desktop';
import type {DismissValue} from './PopoverProps';

/**
 * Matches desktop popover behavior for dismissing when the popover loses focus.
 *
 * When `useReactAriaDismissOutside` is true, desktop popovers use an alternative way of handling this, but we don't support it in bottom sheet popovers since:
 * 1. The differences shouldn't matter for bottom sheets, since they cover the entire screen and no interaction is possible outside of the bottom sheet (see go/jira/RUN_SAILUI-95).
 * 2. It would require a lot of code to support it.
 * 3. It has very few usages.
 */
export function useBottomSheetDismissOnFocusOutsideIntent({
  dismissible,
  onOpenChange,
  contentWrapperRef,
}: {
  dismissible: DismissValue | undefined;
  onOpenChange: (open: boolean) => void;
  contentWrapperRef: React.RefObject<HTMLDivElement>;
}) {
  const uiConfig = useUiConfig();
  const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;
  const [dismissEnabled, dismissOptions] = option(dismissible);
  const shouldDismissOnFocusOutside =
    !shouldApplyIframeOverrides && // Desktop popovers disable the dismiss feature if _unstable_requestIframe is defined
    dismissEnabled &&
    dismissOptions.outsideFocus !== false;
  const dismissOnFocusOutside = useFocusOutside(
    useCallback(() => {
      if (shouldDismissOnFocusOutside) {
        onOpenChange(false);
      }
    }, [shouldDismissOnFocusOutside, onOpenChange]),
    contentWrapperRef.current,
  );

  return assignProps(dismissOnFocusOutside);
}
